import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Gravel calculator with density option
const DENSITIES = { // tonnes per cubic yard (approx.)
  'Pea Gravel': 1.28, 'Crushed Stone #1': 1.35, 'Crushed Stone #57': 1.42,
  'River Rock': 1.35, 'Marble Chips': 1.42, 'Custom': null
};

export default function GravelCalc() {
  const [length, setLength] = useState(20);
  const [width, setWidth] = useState(10);
  const [depth, setDepth] = useState(3);
  const [unit, setUnit] = useState('ft');
  const [depthUnit, setDepthUnit] = useState('in');
  const [type, setType] = useState('Pea Gravel');
  const [density, setDensity] = useState(1.28);
  const [price, setPrice] = useState(30);

  const result = useMemo(() => {
    const toFt = { ft:1, m:3.28084 };
    const depthFt = depthUnit === 'in' ? Number(depth) / 12 : Number(depth) * toFt[unit];
    const cuFt = Number(length) * Number(width) * (toFt[unit] ** 2) * depthFt;
    const cuYd = cuFt / 27;
    const d = type === 'Custom' ? Number(density) : DENSITIES[type];
    const tons = cuYd * d;
    const cost = tons * Number(price);
    return { cuFt, cuYd, tons, cost };
  }, [length, width, depth, unit, depthUnit, type, density, price]);

  return (
    <CalcShell look="cnet" title="Gravel Calculator" subtitle="Estimate cubic yards, tons and cost of gravel for driveways, paths and landscape projects.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Length</label><input type="number" value={length} onChange={e => setLength(e.target.value)}/></div>
            <div className="field"><label>Width</label><input type="number" value={width} onChange={e => setWidth(e.target.value)}/></div>
          </div>
          <div className="field"><label>Area Unit</label>
            <select value={unit} onChange={e => setUnit(e.target.value)}><option value="ft">ft — foot</option><option value="m">m — meter</option></select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Depth</label><input type="number" value={depth} onChange={e => setDepth(e.target.value)}/></div>
            <div className="field"><label>Depth Unit</label>
              <select value={depthUnit} onChange={e => setDepthUnit(e.target.value)}><option value="in">in — inch</option><option value="ft">ft — foot</option><option value="m">m — meter</option></select>
            </div>
          </div>
          <div className="field"><label>Gravel Type</label>
            <select value={type} onChange={e => { setType(e.target.value); if (DENSITIES[e.target.value]) setDensity(DENSITIES[e.target.value]); }}>
              {Object.keys(DENSITIES).map(k => <option key={k}>{k}</option>)}
            </select>
          </div>
          {type === 'Custom' && (
            <div className="field"><label>Density (t/yd³)</label><input type="number" step="0.01" value={density} onChange={e => setDensity(e.target.value)}/></div>
          )}
          <div className="field"><label>Price / ton</label><input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)}/></div>
        </div>
        <div>
          <div className="p-5 rounded-xl bg-stone-100 border border-stone-300 text-center">
            <div className="text-sm text-slate-600">Volume</div>
            <div className="text-3xl font-bold text-stone-800" data-testid="gravel-vol">{result.cuYd.toFixed(2)} yd³</div>
            <div className="text-slate-600">{result.cuFt.toFixed(2)} ft³</div>
            <div className="mt-3 text-sm text-slate-600">Weight</div>
            <div className="text-2xl font-bold text-stone-800">{result.tons.toFixed(2)} tons</div>
            <div className="mt-3 text-sm text-slate-600">Estimated Cost</div>
            <div className="text-2xl font-bold">${result.cost.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
